import clsx from 'clsx'
import { useControlledOrUncontrolledState } from 'src/hooks'
import styles from './styles.module.scss'
import { IAccordionProps } from './types'
import { ReactNode } from 'react'

const Accordion = ({
  chevronAlign = 'right',
  children,
  className,
  defaultIsOpen,
  icon,
  id,
  isOpen,
  onChange,
  size = 'md',
  slots,
  style,
  subtitle,
  title,
  titleAs = 'span',
  variant = 'bordered',
  ...props
}: IAccordionProps) => {
  const {
    isControlled,
    setUncontrolledState,
    state: isOpenState,
  } = useControlledOrUncontrolledState({
    controlledState: isOpen,
    defaultInternalState: defaultIsOpen,
  })

  const handleToggle = () => {
    const newState = !isOpenState
    if (!isControlled) setUncontrolledState(newState)
    onChange?.(newState)
  }

  const TitleComponent = titleAs // Assigning titleAs to a variable to use as a component name
  const accordionIcon: ReactNode = icon ?? (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
      <path d="M256 429.3l22.6-22.6 192-192L493.3 192 448 146.7l-22.6 22.6L256 338.7 86.6 169.4 64 146.7 18.7 192l22.6 22.6 192 192L256 429.3z" />
    </svg>
  )

  return (
    <section
      className={clsx('mui-accordion', styles.accordion, styles[size], styles[variant], className, {
        [styles.open]: isOpenState,
      })}
      data-testid="mui-accordion"
      id={id}
      style={{ ...style }}
      {...props}
    >
      <button
        aria-controls={`control-${id}`}
        aria-expanded={Boolean(isOpenState)}
        className={clsx(styles.button, styles[chevronAlign])}
        data-style="accordion-button"
        onClick={handleToggle}
        type="button"
      >
        {chevronAlign === 'left' && <div className={styles.chevron}>{accordionIcon}</div>}
        {slots && slots.before && <div className={styles.slot}>{slots.before}</div>}

        <TitleComponent className={styles.title} id={`control-${id}`}>
          {title}
        </TitleComponent>

        {subtitle && <p className={clsx('body-14-regular', styles.subtitle)}>{subtitle}</p>}
        {slots && slots.after && <div className={styles.slot}>{slots.after}</div>}
        {chevronAlign === 'right' && <div className={styles.chevron}>{accordionIcon}</div>}
      </button>
      <div
        aria-labelledby={`control-${id}`}
        className={clsx('body-16-regular', styles.content)}
        data-style="content"
        role="region"
      >
        {slots?.beforeContent}
        <div data-style="inner">{children}</div>
      </div>
    </section>
  )
}

export default Accordion
